import { DateTime } from '@ksyos/date-time';
import { FormGroupContext, Modal } from '@ksyos/design-system';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { MaritalStatus } from '@common/enums';
import type { IPatientAddEditValidated } from '@common/validation/patient';
import type {
    CareProviderOrganizationsPractitioners,
    OrganizationPractitionerOptions,
} from '@frontend/components/elements/CareProviderSelection';
import type { IOption } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization, PatientDetailsChangeSource, State } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { patientAddEditSchema } from '@common/validation/patient';
import { Loader, Loading } from '@frontend/components/elements/common';
import { fetchJson } from '@frontend/store/fetch';
import { FetchError } from '@frontend/store/fetchError';
import { getSelectedLanguage } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    scrollValidationErrorIntoView,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { getCountryOption } from '@frontend/utils/getCountryOption';
import type { IPatient } from './PatientsAddEdit';
import { PatientDeceasedEditForm } from './PatientDeceasedEditForm';
import { PatientEditForm } from './PatientEditForm';
import { PatientActiveInsuranceContext } from './PatientInsuranceHistoryContext';
import { PatientDeathModal } from './patientSideBar/PatientDeathModal';

interface IEditPatientModalProps {
    isOpen: boolean;
    onClose: () => Promise<void> | void;
    onSavePatientDetails: () => Promise<void> | void;
    params: {
        id?: string;
        ssoId?: string;
    };
}
export function EditPatientModal(props: IEditPatientModalProps) {
    const { activeInsurance } = useContext(PatientActiveInsuranceContext);

    const [patient, setPatient] = useState<IPatient | null>(null);
    const [isSearching, setIsSearching] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [gpOptions, setGpOptions] = useState<IOption<string>[]>([]);
    const [patientConsent, setPatientConsent] = useState<boolean>(false);
    const [isPatientDeathModalOpen, setIsPatientDeathModalOpen] = useState<boolean>(false);
    const [deceasedDate, setDeceasedDate] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const countryOptions: IOption<string>[] = getCountryOptions();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    if (isManager) {
        assertHasAuthorization(ManagerAuthorization.patientEdit);
    }

    useEffect(() => {
        loadPatient();
        loadGeneralPractitionerOptions();
        setValidationErrors(emptyValidationErrors);
        setPatientConsent(false);
    }, []);

    useEffect(() => {
        setPatientConsent(!!deceasedDate);
    }, [deceasedDate]);

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                onClose={props.onClose}
                size="large"
            >
                <Modal.Header>{getSelectedLanguage().patient.detailsCounselling}</Modal.Header>
                <Modal.CloseButton />
                <FormGroupContext.Provider
                    value={{
                        labelWidth: 'one-third',
                        size: 'medium',
                        variant: 'outline',
                    }}
                >
                    <Loading
                        item={patient}
                        loaded={(loadedPatient) => (
                            <>
                                {isSearching ? (
                                    <Modal.Body>
                                        <Loader
                                            fixedCentered
                                            horizontalCentered
                                        />
                                    </Modal.Body>
                                ) : !deceasedDate || isPatientDeathModalOpen ? (
                                    <PatientEditForm
                                        activeInsurance={activeInsurance}
                                        countryOptions={countryOptions}
                                        getCountryOption={getCountryOption}
                                        getMaritalStatusOption={getMaritalStatusOption}
                                        getPractitionerAndOrganizationOption={getPractitionerAndOrganizationOption}
                                        getStateOption={getStateOption}
                                        gpOptions={gpOptions}
                                        handleSubmit={handleSubmit}
                                        isSaving={isSaving}
                                        loadedPatient={loadedPatient}
                                        onClose={props.onClose}
                                        patient={patient}
                                        patientConsent={patientConsent}
                                        setIsPatientDeathModalOpen={setIsPatientDeathModalOpen}
                                        setPatient={setPatient}
                                        setPatientConsent={setPatientConsent}
                                        setValidationErrors={setValidationErrors}
                                        updateAndValidateBirthDate={updateAndValidateBirthDate}
                                        validateOne={validateOne}
                                        validationErrors={validationErrors}
                                    />
                                ) : (
                                    <PatientDeceasedEditForm
                                        activeInsurance={activeInsurance}
                                        deceasedDate={deceasedDate}
                                        getCountryOption={getCountryOption}
                                        getMaritalStatusOption={getMaritalStatusOption}
                                        getPractitionerAndOrganizationOption={getPractitionerAndOrganizationOption}
                                        getStateOption={getStateOption}
                                        handleSubmit={handleSubmit}
                                        isSaving={isSaving}
                                        loadedPatient={loadedPatient}
                                        onClose={props.onClose}
                                        setDeceasedDate={setDeceasedDate}
                                    />
                                )}
                            </>
                        )}
                    />
                </FormGroupContext.Provider>
            </Modal>
            <PatientDeathModal
                deceasedDate={deceasedDate}
                getDateValidationError={getDateValidationError}
                isPatientDeathModalOpen={isPatientDeathModalOpen}
                patient={patient}
                setDeceasedDate={setDeceasedDate}
                setIsPatientDeathModalOpen={setIsPatientDeathModalOpen}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
            />
        </>
    );

    function validateOne(name: keyof IPatient) {
        setValidationErrors(updateValidationErrorsForOne(patientAddEditSchema, patient, validationErrors, name));
    }

    async function loadPatient() {
        setIsSearching(true);
        const url = `/api/patients/${props.params.id}`;
        const result = await fetchJson(url, 'GET');
        setPatient(result.json);
        setDeceasedDate(result.json.deceasedDate);
        setIsSearching(false);
    }

    async function handleSubmit() {
        const { errors, item } = validate(patientAddEditSchema, patient);
        setValidationErrors(errors);

        if (errors.size > 0) {
            scrollValidationErrorIntoView();

            return;
        }
        const url = `/api/patients/${props.params.id}?patientDetailsChangeSource=${PatientDetailsChangeSource.patient}`;
        const data: { patient: IPatientAddEditValidated | Record<string, never> } = {
            patient: {
                ...(item as IPatientAddEditValidated),
                deceasedDate: deceasedDate ?? null,
                isDeceased: !!deceasedDate,
            },
        };

        setIsSaving(true);

        try {
            await fetchJson(url, 'POST', data);

            await props.onSavePatientDetails();

            await props.onClose();
        } catch (err) {
            if (err instanceof FetchError) {
                if (
                    err.apiErrorResponse?.statusCode === 409 &&
                    err.apiErrorResponse?.errorCode === 'Duplicate Patient'
                ) {
                    setValidationErrors(validationErrors.set('identifierSocialSecurity', 'bsnAlreadyExists'));
                    scrollValidationErrorIntoView();
                }
            }
        }

        setIsSaving(false);
    }

    async function loadGeneralPractitionerOptions() {
        const organizations = await loadOrganizationsPractitioners();
        const options: IOption<string>[] = [];
        organizations.map((organization) => {
            for (const practitioner of organization.practitioners) {
                options.push({
                    label: `${practitioner.label}, ${organization.label}`,
                    value: `${practitioner.value}, ${organization.value}`,
                });
            }
        });

        setGpOptions(options);
    }

    async function loadOrganizationsPractitioners() {
        const urlBuilder = new UrlBuilder('/api/organizations/organizationsPractitioners');
        urlBuilder.add('practitionerIsActivated', true);
        urlBuilder.add('organizationIsActive', true);

        const url = urlBuilder.build();
        const result = await fetchJson(url, 'GET');

        const organizationWithGeneralPractitioners: OrganizationPractitionerOptions[] = [];
        for (const organization of result.json.items as CareProviderOrganizationsPractitioners[]) {
            const practitionerOptions: IOption<string>[] = [];
            for (const practitioner of organization.practitioners) {
                practitionerOptions.push({
                    label: getDisplayName(practitioner),
                    value: `${practitioner.id}`,
                });
            }

            organizationWithGeneralPractitioners.push({
                disabled: true,
                label: createOrganizationLabel(organization),
                practitioners: practitionerOptions,
                value: organization.id,
            });
        }

        return organizationWithGeneralPractitioners;
    }

    function createOrganizationLabel(organization: CareProviderOrganizationsPractitioners) {
        return `${organization.name} ${organization.city ? `(${organization.city})` : ''}`;
    }

    function getPractitionerAndOrganizationOption(organizationId: string, practitionerId: string) {
        const selectedOption = gpOptions.find((option) => option.value === `${practitionerId}, ${organizationId}`);

        return selectedOption;
    }

    function getMaritalStatusOption(maritalStatus: MaritalStatus | null) {
        if (maritalStatus) {
            return {
                label: getSelectedLanguage().maritalStatusEnum[maritalStatus],
                value: `${maritalStatus}`,
            };
        }
    }

    function getStateOption(state: string | null) {
        if (state) {
            return {
                label: Object.entries(State).find((item) => item[0] === state)?.[1],
                value: state,
            };
        }
    }

    function isDateInTheFuture(date: string) {
        const today = new Date();

        return DateTime.from(today).isBefore(date, 'days');
    }

    function getDateValidationError(newDate: string | null) {
        if (newDate) {
            if (!DateTime.from(newDate).isValid()) {
                return 'koi_date';
            }
            if (isDateInTheFuture(newDate)) {
                return 'date_future';
            }
        }
    }

    function updateAndValidateBirthDate(loadedPatient: IPatient, date: string | null) {
        const dateErrors = getDateValidationError(date);

        if (dateErrors) {
            setValidationErrors(validationErrors.set('birthDate', dateErrors));
        }
        if (!dateErrors) {
            setValidationErrors(validationErrors.remove('birthDate'));
            setPatient({
                ...loadedPatient,
                birthDate: date,
            });
        }
    }

    function getCountryOptions() {
        const result: IOption<string>[] = Object.entries(getSelectedLanguage().Countries).map(([value, label]) => {
            return { label, value };
        });

        return result;
    }
}
